import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LucideAngularModule, User, ChevronDown, MessageSquare } from 'lucide-angular';
import { AuthService } from '../../../core/services/auth.service';
import { User as UserModel } from '../../../core/models/auth.model';
import { PatrolAlertsComponent } from '../../../features/patrol-alerts/components/alerts-component/patrol-alerts.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, LucideAngularModule, PatrolAlertsComponent],
  template: `
    <header
      class="sticky top-0 z-50 h-16 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-6 transition-all"
    >
      <div class="flex items-center space-x-6">
        <a
          routerLink="/"
          aria-label="Ir para Dashboard"
          class="flex items-center transition-opacity hover:opacity-90 dynamic-native-focus"
        >
          <img
            src="/logo.png"
            alt="Company Logo"
            class="h-9 w-auto object-contain"
            (error)="onLogoError($event)"
          />
        </a>

        <div class="flex items-center space-x-4">
          <span
            *ngIf="(pageTitle && pageTitle !== 'Dashboard') || breadcrumbs.length > 0"
            class="h-5 w-px bg-gray-200"
          ></span>

          <h2
            *ngIf="pageTitle && pageTitle !== 'Dashboard' && breadcrumbs.length === 0"
            class="text-sm font-semibold text-gray-900 tracking-tight"
          >
            {{ pageTitle }}
          </h2>

          <nav *ngIf="breadcrumbs.length > 0" aria-label="Breadcrumb">
            <ol class="flex items-center space-x-2 text-xs font-medium text-gray-500">
              <li
                *ngFor="let crumb of breadcrumbs; let last = last"
                class="flex items-center space-x-2"
              >
                <span *ngIf="!last" class="hover:text-gray-900 cursor-pointer transition-colors">{{
                  crumb
                }}</span>
                <span *ngIf="last" class="text-gray-900 font-semibold">{{ crumb }}</span>
                <span *ngIf="!last" class="text-gray-300 font-normal select-none">/</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div class="flex items-center space-x-2">
        <a
          (click)="navigateToChat()"
          class="flex items-center px-3 py-2.5 text-sm font-medium rounded-lg cursor-pointer transition-colors"
        >
          <lucide-icon [img]="MessageSquare" [size]="18"></lucide-icon>
          <!-- <span class="flex-1">Chat</span> -->
        </a>
        <div class="flex items-center justify-center min-w-[2.5rem]">
          <app-patrol-alerts />
        </div>

        <!-- <span class="h-5 w-px bg-gray-150"></span> -->

        <button
          (click)="onOpenUserProfile()"
          class="group flex items-center space-x-3 p-1.5 pr-3 rounded-xl hover:bg-gray-50 active:bg-gray-100/80 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200 text-left"
        >
          <div
            class="w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-500 group-hover:text-gray-700 transition-colors"
          >
            <lucide-icon [img]="UserIcon" class="w-4 h-4"></lucide-icon>
          </div>

          <div class="hidden sm:flex flex-col">
            <span
              class="text-xs font-semibold text-gray-800 leading-tight group-hover:text-gray-950 transition-colors"
            >
              {{ getUserDisplayName() }}
            </span>
            <span
              *ngIf="getUserRole()"
              class="text-[10px] font-medium text-gray-400 tracking-wider uppercase mt-0.5"
            >
              {{ getUserRole() }}
            </span>
          </div>

          <lucide-icon
            [img]="ChevronDownIcon"
            class="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors"
          ></lucide-icon>
        </button>
      </div>
    </header>
  `,
})
export class NavbarComponent implements OnInit {
  @Input() pageTitle = 'Dashboard';
  @Input() breadcrumbs: string[] = [];
  @Output() openUserProfile = new EventEmitter<void>(); // Apenas o Output

  currentUser: UserModel | null = null;

  readonly UserIcon = User;
  readonly ChevronDownIcon = ChevronDown;
  readonly MessageSquare = MessageSquare;

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
  }

  // Método que emite o evento
  onOpenUserProfile(): void {
    this.openUserProfile.emit();
  }

  getUserDisplayName(): string {
    return this.currentUser
      ? `${this.currentUser.first_name} ${this.currentUser.last_name}`
      : 'Usuário';
  }

  getUserRole(): string {
    if (!this.currentUser) return '';

    const roleLabels: { [key: string]: string } = {
      admin: 'Administrador',
      manager: 'Gerente',
      supervisor: 'Supervisor',
      operator: 'Operador',
      user: 'Usuário',
      guard: 'Vigilante',
    };

    return (
      roleLabels[this.currentUser?.role_settings?.designation || this.currentUser?.role] ||
      this.currentUser?.role
    );
  }
  navigateToChat(): void {
    const currentUser = this.authService.getCurrentUser();
    const companyId = currentUser?.company_id;

    const baseUrl = window.location.origin;

    const url = companyId ? `${baseUrl}/chat?company_id=${companyId}` : `${baseUrl}/chat`;

    window.open(url, '_blank', 'noopener,noreferrer');
  }

  onLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = '/logo.png';
  }
}
